import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { Checkbox } from './ui/checkbox';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Shield, Plus, Pencil, Trash2, Loader2, Search, Save, ChevronDown, ChevronRight, Lock,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function RolesManager({ token }) {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null);
  const [rolePerms, setRolePerms] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', description: '', scope: 'global', tenant_id: '' });
  const [expandedCats, setExpandedCats] = useState(new Set());
  const [search, setSearch] = useState('');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = useCallback(async () => {
    try {
      const [rolesRes, permsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/roles`, { headers }),
        axios.get(`${BACKEND_URL}/api/permissions`, { headers }),
      ]);
      // Filter out super_admin from tenant admin view (only shown in Global Admin)
      const allRoles = rolesRes.data.roles || [];
      setRoles(allRoles.filter(r => r.id !== 'super_admin'));
      setPermissions(permsRes.data.permissions || []);
      setGrouped(permsRes.data.grouped || {});
    } catch { toast.error('Failed to load roles'); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const selectRole = (role) => {
    setSelectedRole(role);
    const perms = role.permissions || [];
    setRolePerms(new Set(perms.includes('*') ? permissions.map(p => p.id) : perms));
    // Expand all categories
    setExpandedCats(new Set(Object.keys(grouped)));
  };

  const togglePerm = (permId) => {
    if (selectedRole?.is_system && selectedRole?.permissions?.includes('*')) return;
    setRolePerms(prev => {
      const next = new Set(prev);
      next.has(permId) ? next.delete(permId) : next.add(permId);
      return next;
    });
  };

  const toggleCategory = (cat) => {
    const catPerms = grouped[cat]?.map(p => p.id) || [];
    const allChecked = catPerms.every(p => rolePerms.has(p));
    setRolePerms(prev => {
      const next = new Set(prev);
      catPerms.forEach(p => allChecked ? next.delete(p) : next.add(p));
      return next;
    });
  };

  const savePermissions = async () => {
    if (!selectedRole) return;
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/roles/${selectedRole.id}`, {
        permissions: Array.from(rolePerms),
      }, { headers });
      toast.success(`Permissions updated for ${selectedRole.name}`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const openCreate = () => {
    setEditForm({ name: '', description: '', scope: 'global', tenant_id: '' });
    setSelectedRole(null);
    setEditDialog(true);
  };

  const createRole = async () => {
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/roles`, editForm, { headers });
      toast.success('Role created');
      setEditDialog(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
    finally { setSaving(false); }
  };

  const deleteRole = async (roleId) => {
    if (!window.confirm('Delete this role?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/roles/${roleId}`, { headers });
      toast.success('Role deleted');
      if (selectedRole?.id === roleId) setSelectedRole(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Cannot delete'); }
  };

  const filteredPerms = useMemo(() => {
    if (!search) return grouped;
    const q = search.toLowerCase();
    const result = {};
    for (const [cat, perms] of Object.entries(grouped)) {
      const filtered = perms.filter(p => p.label.toLowerCase().includes(q) || p.id.toLowerCase().includes(q));
      if (filtered.length > 0 || cat.toLowerCase().includes(q)) result[cat] = filtered.length > 0 ? filtered : perms;
    }
    return result;
  }, [grouped, search]);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const isWildcard = selectedRole?.permissions?.includes('*');

  return (
    <div className="flex h-full" data-testid="roles-manager">
      {/* Role List */}
      <div className="w-64 border-r bg-muted/20 flex flex-col shrink-0">
        <div className="p-4 border-b space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Roles</h3>
            <Button variant="outline" size="sm" onClick={openCreate} data-testid="create-role-btn" className="h-7 gap-1 text-xs">
              <Plus className="h-3 w-3" /> New
            </Button>
          </div>
        </div>
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            {roles.map(r => (
              <button key={r.id} onClick={() => selectRole(r)} data-testid={`role-${r.id}`}
                className={`w-full text-left p-3 rounded-lg transition-colors text-sm ${
                  selectedRole?.id === r.id ? 'bg-primary/10 border border-primary/20' : 'hover:bg-muted/60'
                }`}>
                <div className="flex items-center gap-2">
                  {r.is_system && <Lock className="h-3 w-3 text-muted-foreground shrink-0" />}
                  <span className="font-medium truncate">{r.name}</span>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className="text-[9px] h-4">{r.scope}</Badge>
                  <span className="text-[10px] text-muted-foreground">
                    {r.permissions?.includes('*') ? 'All' : r.permissions?.length || 0} perms
                  </span>
                </div>
              </button>
            ))}
          </div>
        </ScrollArea>
      </div>

      {/* Permission Matrix */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {selectedRole ? (
          <>
            <div className="p-5 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>{selectedRole.name}</h2>
                    {selectedRole.is_system && <Badge variant="secondary" className="text-[10px]"><Lock className="h-2.5 w-2.5 mr-0.5" /> System</Badge>}
                    <Badge variant="outline" className="text-[10px]">{selectedRole.scope}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{selectedRole.description}</p>
                </div>
                <div className="flex gap-2">
                  {!isWildcard && (
                    <Button onClick={savePermissions} disabled={saving} size="sm" className="gap-1 text-xs" data-testid="save-perms-btn">
                      <Save className="h-3 w-3" /> {saving ? 'Saving...' : 'Save Permissions'}
                    </Button>
                  )}
                  {!selectedRole.is_system && (
                    <Button variant="outline" size="sm" onClick={() => deleteRole(selectedRole.id)} className="gap-1 text-xs text-destructive hover:text-destructive">
                      <Trash2 className="h-3 w-3" /> Delete
                    </Button>
                  )}
                </div>
              </div>
              <div className="relative mt-3 max-w-sm">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input placeholder="Filter permissions..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8 h-8 text-xs" />
              </div>
            </div>

            <ScrollArea className="flex-1 p-5">
              {isWildcard && (
                <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs">
                  This role has wildcard (*) access — all permissions are granted. To customize, remove wildcard and assign individual permissions.
                </div>
              )}
              <div className="space-y-3 max-w-3xl">
                {Object.entries(filteredPerms).map(([cat, perms]) => {
                  const expanded = expandedCats.has(cat);
                  const catPerms = perms.map(p => p.id);
                  const checkedCount = catPerms.filter(p => rolePerms.has(p)).length;
                  const allChecked = checkedCount === catPerms.length;
                  return (
                    <div key={cat} className="border rounded-lg overflow-hidden">
                      <button onClick={() => setExpandedCats(prev => {
                          const n = new Set(prev); n.has(cat) ? n.delete(cat) : n.add(cat); return n;
                        })}
                        className="w-full flex items-center justify-between p-3 hover:bg-muted/40 transition-colors">
                        <div className="flex items-center gap-2">
                          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          <span className="text-sm font-medium">{cat}</span>
                          <Badge variant="secondary" className="text-[10px]">{checkedCount}/{catPerms.length}</Badge>
                        </div>
                        {!isWildcard && (
                          <Checkbox checked={allChecked} onCheckedChange={() => toggleCategory(cat)} onClick={e => e.stopPropagation()} />
                        )}
                      </button>
                      {expanded && (
                        <div className="border-t px-3 py-2 space-y-1 bg-muted/10">
                          {perms.map(p => (
                            <label key={p.id} className="flex items-center gap-3 py-1.5 px-2 rounded hover:bg-muted/30 cursor-pointer">
                              <Checkbox checked={rolePerms.has(p.id)} onCheckedChange={() => togglePerm(p.id)} disabled={isWildcard} />
                              <div className="flex-1 min-w-0">
                                <span className="text-sm">{p.label}</span>
                                {p.description && <p className="text-[10px] text-muted-foreground leading-tight mt-0.5">{p.description}</p>}
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </>
        ) : (
          <div className="h-full flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <Shield className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Select a role to manage permissions</p>
              <p className="text-xs mt-1">or create a new one</p>
            </div>
          </div>
        )}
      </div>

      {/* Create Role Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Role</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium">Name</label>
              <Input value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
                placeholder="Regional Director" className="mt-1" data-testid="role-name-input" />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Input value={editForm.description} onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Access for regional property directors" className="mt-1" />
            </div>
            <div>
              <label className="text-sm font-medium">Scope</label>
              <div className="flex gap-2 mt-1">
                {['global', 'tenant'].map(s => (
                  <button key={s} onClick={() => setEditForm(p => ({ ...p, scope: s }))}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${editForm.scope === s ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted/60'}`}>
                    {s === 'global' ? 'Global (all tenants)' : 'Tenant-specific'}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>Cancel</Button>
            <Button onClick={createRole} disabled={saving || !editForm.name}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Create Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
